<template>
  <!-- Active Filter Chips -->
  <div v-if="filterChips.length > 0" class="px-4 py-2 d-flex flex-wrap ga-2 align-center">
    <v-chip
      v-for="chip in filterChips"
      :key="chip.key"
      closable
      size="small"
      @click:close="removeFilter(chip.key)"
    >
      {{ chip.label }}: {{ chip.value }}
    </v-chip>
    <v-btn
      v-if="filterChips.length > 1"
      variant="text"
      size="x-small"
      color="error"
      @click="clearFilters"
    >
      Clear all
    </v-btn>
  </div>

  <!-- Filter Dialog -->
  <v-dialog v-model="isFilterExpanded" :fullscreen="mobile" :max-width="mobile ? undefined : 700">
    <v-card class="d-flex flex-column" style="max-height: 90vh;">
      <v-toolbar color="primary" density="compact" class="flex-shrink-0">
        <v-toolbar-title>Filter Sales Data</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :size="mobile ? 'default' : 'small'" @click="toggleFilterExpand">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4 flex-grow-1 overflow-y-auto">
        <!-- Location Filters -->
        <div class="text-caption text-medium-emphasis mb-2">Location & Source</div>
        <v-row class="mb-4">
          <v-col cols="6" sm="4">
            <v-select
              v-model="filters.city"
              :items="options.cities"
              item-title="name"
              item-value="name"
              label="City"
              clearable
              density="comfortable"
              variant="outlined"
              hide-details
              :loading="loading"
            ></v-select>
          </v-col>
          <v-col cols="6" sm="4">
            <v-select
              v-model="filters.location"
              :items="options.locations"
              item-title="name"
              item-value="name"
              label="Location"
              clearable
              density="comfortable"
              variant="outlined"
              hide-details
              :loading="loading"
            ></v-select>
          </v-col>
          <v-col cols="6" sm="4">
            <v-select
              v-model="filters.brand"
              :items="options.brands"
              item-title="name"
              item-value="name"
              label="Brand"
              clearable
              density="comfortable"
              variant="outlined"
              hide-details
              :loading="loading"
            ></v-select>
          </v-col>
          <v-col cols="6" sm="4">
            <v-select
              v-model="filters.platform"
              :items="options.platforms"
              item-title="name"
              item-value="name"
              label="Platform"
              clearable
              density="comfortable"
              variant="outlined"
              hide-details
              :loading="loading"
            >
              <template v-slot:item="{ item, props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <div :style="{ background: getPlatformGradient(item.name), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <div :style="{ background: getPlatformGradient(item.name), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                  {{ item.name }}
                </div>
              </template>
            </v-select>
          </v-col>
          <v-col cols="6" sm="4">
            <v-select
              v-model="filters.source"
              :items="options.sources"
              label="Source"
              clearable
              density="comfortable"
              variant="outlined"
              hide-details
              :loading="loading"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Options -->
        <v-row class="mb-4">
          <v-col cols="6" sm="4">
            <v-checkbox
              v-model="excludeZero"
              label="Exclude zero sales"
              density="compact"
              hide-details
            ></v-checkbox>
          </v-col>
        </v-row>

        <!-- Date Filters -->
        <v-row class="align-start">
          <!-- Sales Date Range -->
          <v-col cols="12" sm="6">
            <div class="text-caption text-medium-emphasis mb-2">Sales Date</div>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="filters.start_date"
                  label="From"
                  type="date"
                  :max="filters.end_date"
                  clearable
                  density="comfortable"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="filters.end_date"
                  label="To"
                  type="date"
                  :min="filters.start_date"
                  clearable
                  density="comfortable"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>

          <!-- Entered Date Range -->
          <v-col cols="12" sm="6">
            <div class="text-caption text-medium-emphasis mb-2">Entered Date</div>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="filters.created_at_start"
                  label="From"
                  type="date"
                  :max="filters.created_at_end"
                  clearable
                  density="comfortable"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="filters.created_at_end"
                  label="To"
                  type="date"
                  :min="filters.created_at_start"
                  clearable
                  density="comfortable"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Quick Date Preset -->
        <v-row class="mt-2">
          <v-col cols="12" sm="4">
            <div class="text-caption text-medium-emphasis mb-2">Quick Select</div>
            <v-select
              v-model="dateRangePreset"
              :items="datePresets"
              item-title="label"
              item-value="value"
              label="Preset"
              clearable
              density="comfortable"
              variant="outlined"
              hide-details
              @update:model-value="applyDatePreset"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="flex-shrink-0">
        <v-btn variant="outlined" prepend-icon="mdi-filter-remove" @click="clearFilters" :disabled="loading || !hasActiveFilters">Clear</v-btn>
        <v-spacer />
        <v-btn color="primary" variant="flat" prepend-icon="mdi-filter" @click="toggleFilterExpand">Apply</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { useFilter } from "@/composables/useFilter.js";
import { useApp } from "@/composables/useApp.js";
import { usePlatformColors } from "@/composables/usePlatformColors.js";
import { useDisplay } from "vuetify";

const {
  isFilterExpanded,
  filters,
  loading,
  hasActiveFilters,
  datePresets,
  dateRangePreset,
  clearFilters,
  applyDatePreset,
  toggleFilterExpand,
  filterChips,
  removeFilter,
} = useFilter();

const { fetchOptions, options } = useApp();
const { getPlatformGradient } = usePlatformColors();

const { mobile } = useDisplay()

const excludeZero = computed({
  get: () => filters.exclude_zero === "true",
  set: (val) => { filters.exclude_zero = val ? "true" : ""; },
});

// Auto-set end date when start date is entered
watch(
  () => filters.start_date,
  (newVal) => {
    if (newVal && !filters.end_date) {
      filters.end_date = newVal;
    }
  },
);

// Auto-set entered end date when start date is entered
watch(
  () => filters.created_at_start,
  (newVal) => {
    if (newVal && !filters.created_at_end) {
      filters.created_at_end = newVal;
    }
  },
);

onMounted(async () => {
  await fetchOptions();
});
</script>
